import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getAuthToken } from './authService';

console.log('🔌 API CLIENT - Initializing with environment:', {
  NODE_ENV: process.env.NODE_ENV,
  API_URL: process.env.REACT_APP_API_URL
});

// Create a base axios instance with common configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add detailed logging and debugging
const logRequest = (config: InternalAxiosRequestConfig) => {
  const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
  console.log(`🔌 API REQUEST: ${config.method?.toUpperCase()} ${fullUrl}`);
  console.log('🔌 Request headers:', JSON.stringify(config.headers));
  if (config.data) {
    console.log('🔌 Request payload:', typeof config.data === 'string' ? config.data : JSON.stringify(config.data));
  }
  if (config.params) {
    console.log('🔌 Request params:', JSON.stringify(config.params));
  }
  return config;
};

// Add request interceptor to add auth token to every request
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Get the current auth token
      const token = await getAuthToken();
      console.log(`🔌 AUTH DEBUG for ${config.url}: Token retrieved:`, token ? `${token.substring(0, 10)}...` : 'No token');
      
      // For debugging purposes, also try to get the token directly from localStorage
      const localStorageToken = localStorage.getItem('admin_auth_token');
      console.log(`🔌 AUTH DEBUG for ${config.url}: localStorage token:`, localStorageToken ? 'Token exists' : 'No token in localStorage');
      
      // Add the auth token to the headers if we have one
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Log the request details for debugging
      return logRequest(config);
    } catch (error) {
      console.error('Error setting auth token:', error);
      return logRequest(config);
    }
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for logging responses
apiClient.interceptors.response.use(
  (response) => {
    console.log(`🔌 API RESPONSE: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    console.log('🔌 Response data structure:', {
      hasData: response.data && typeof response.data === 'object' && 'data' in response.data,
      topLevelKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : 'not an object',
      dataType: response.data && response.data.data ? typeof response.data.data : 'no data property'
    });
    return response;
  },
  (error) => {
    if (error.response) {
      console.error(`🔌 API ERROR ${error.response.status}: ${error.config.method?.toUpperCase()} ${error.config.url}`);
      console.error('🔌 Error response:', error.response.data);
    } else if (error.request) {
      console.error('🔌 API REQUEST ERROR: No response received');
      console.error('🔌 Request details:', error.request);
    } else {
      console.error('🔌 API CONFIG ERROR:', error.message);
    }
    return Promise.reject(error);
  }
);

// Helper to safely extract data from API responses
const extractResponseData = <T>(response: any): T => {
  // Check if the response has a nested data property (standard API format)
  if (response && typeof response === 'object' && 'data' in response) {
    console.log('🔌 Extracting nested data from response');
    return response.data as T;
  }
  
  // Fallback to returning the whole response if it doesn't match expected format
  console.log('🔌 Response does not have expected structure, returning as-is');
  return response as T;
};

// Export methods for common API operations
export const api = {
  get: async<T>(url: string, config?: InternalAxiosRequestConfig): Promise<T> => {
    console.log(`🔌 API client get() called with URL: ${url}`);
    const response = await apiClient.get(url, config);
    const extractedData = extractResponseData<T>(response.data);
    console.log('🔌 Extracted data type:', Array.isArray(extractedData) ? 'array' : typeof extractedData);
    if (Array.isArray(extractedData)) {
      console.log('🔌 Array length:', extractedData.length);
    }
    return extractedData;
  },
  
  post: async<T>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> => {
    console.log(`🔌 API client post() called with URL: ${url}`);
    
    // Special logging for enrollment requests
    if (url.includes('/enrollments')) {
      console.log('🔌 ENROLLMENT DEBUG: POST data:', data);
      if (data && typeof data === 'object') {
        console.log('🔌 ENROLLMENT DEBUG: user_id type:', typeof data.user_id);
        console.log('🔌 ENROLLMENT DEBUG: user_id value:', data.user_id);
      } else {
        console.log('🔌 ENROLLMENT DEBUG: data is undefined or not an object, using query parameters');
      }
    }
    
    const response = await apiClient.post(url, data, config);
    return extractResponseData<T>(response.data);
  },
  
  put: async<T>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> => {
    console.log(`🔌 API client put() called with URL: ${url}`);
    const response = await apiClient.put(url, data, config);
    return extractResponseData<T>(response.data);
  },
  
  patch: async<T>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> => {
    console.log(`🔌 API client patch() called with URL: ${url}`);
    const response = await apiClient.patch(url, data, config);
    return extractResponseData<T>(response.data);
  },
  
  delete: async<T>(url: string, config?: InternalAxiosRequestConfig): Promise<T> => {
    console.log(`🔌 API client delete() called with URL: ${url}`);
    const response = await apiClient.delete(url, config);
    return extractResponseData<T>(response.data);
  },
};

export default api;
